import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, Calendar, Clock, TrendingUp, AlertTriangle } from 'lucide-react';
import { ChildIcon, genderBg, genderColor, genderAccent, genderBorder } from '../components/ChildIcon';
import { ChildFilterCards } from '../components/ChildFilterCards';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getAbsenceLabel, API_BASE as API } from '../utils/constants';

const EntryCard = ({ entry, validating, onValidate, showActions = false, testIdPrefix = '', contracts = [] }) => {
  const contract = contracts.find(c => c.id === entry.contract_id);
  const childSex = contract?.child_sex || 'M';
  const childName = contract?.child_first_name || entry.child_name || 'Inconnu';

  return (
    <Card 
      key={entry.id} 
      className={cn(
        "hover:shadow-md transition-all duration-200",
        entry.is_absent && "bg-[var(--warning)]/5 border-[var(--warning)]/40",
        !entry.is_absent && entry.status === 'validated' && "bg-[var(--success)]/5 border-[var(--success)]/30",
      )} 
      data-testid={`${testIdPrefix}-entry-${entry.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div 
              className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
              style={{ 
                backgroundColor: entry.is_absent
                  ? 'var(--warning)' + '15'
                  : entry.status === 'validated'
                    ? 'var(--success)' + '15'
                    : genderBg(childSex),
              }}
            >
              {entry.is_absent 
                ? <AlertTriangle className="h-4 w-4 text-[var(--warning)]" />
                : <ChildIcon sex={childSex} className="h-4 w-4" style={{ color: genderColor(childSex) }} />
              }
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm truncate" style={{ color: 'var(--text-primary)' }}>{childName}</p>
              <p className="text-xs text-[var(--text-secondary)]">
                {new Date(entry.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
              {entry.is_absent && (entry.absence_type || entry.absence_reason) && (
                <p className="text-xs text-[var(--warning)] mt-0.5 font-medium">
                  {entry.absence_type ? getAbsenceLabel(entry.absence_type) : entry.absence_reason}
                  {entry.absence_notes && ` — ${entry.absence_notes}`}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <p className={cn(
                "font-mono font-semibold text-lg",
                entry.is_absent ? "text-[var(--warning)]" : 
                entry.status === 'validated' ? "text-[var(--success)]" : "text-[var(--primary-400)]"
              )}>
                {entry.is_absent ? '—' : `${entry.hours_worked || 0}h`}
              </p>
              <Badge variant="outline" className={cn(
                "text-[10px]",
                entry.status === 'pending' && 'bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)]',
                entry.status === 'validated' && 'bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]',
                entry.status === 'rejected' && 'bg-[var(--error)]/10 text-[var(--error)] border-[var(--error)]',
              )}>
                {entry.status === 'pending' ? 'En attente' : entry.status === 'validated' ? 'Validé' : 'Rejeté'}
              </Badge>
            </div>
            {showActions && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => onValidate(entry.id, 'validated')}
                  disabled={validating === entry.id}
                  className="bg-[var(--success)] hover:bg-[var(--success)]/90"
                  data-testid="validate-button"
                >
                  <CheckCircle className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onValidate(entry.id, 'rejected')}
                  disabled={validating === entry.id}
                  className="border-[var(--error)] text-[var(--error)] hover:bg-[var(--error)]/10"
                  data-testid="reject-button"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TimeValidation = ({ embedded = false }) => {
  const { user } = useAuth();
  const [timeEntries, setTimeEntries] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(null);
  const [selectedChild, setSelectedChild] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Build month options (6 months back + current)
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase());
      opts.push({ value: val, label });
    }
    return opts;
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [selectedMonth]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [entriesRes, contractsRes] = await Promise.all([
        axios.get(`${API}/time/entries?month=${selectedMonth}`),
        axios.get(`${API}/contracts`),
      ]);
      setTimeEntries(entriesRes.data);
      setContracts(contractsRes.data.filter(c => c.status === 'active'));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  const handleValidate = async (entryId, newStatus) => {
    try {
      setValidating(entryId);
      await axios.post(`${API}/time/validate`, { entry_ids: [entryId], action: newStatus === 'validated' ? 'validate' : 'reject' });
      toast.success(newStatus === 'validated' ? 'Heures validées !' : 'Heures rejetées');
      await fetchData();
    } catch (error) {
      console.error('Error validating:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setValidating(null);
    }
  };
  
  const handleValidateAll = async () => {
    if (!window.confirm('Valider toutes les heures en attente ?')) return;
    
    try {
      setValidating('all');
      const pendingIds = filteredEntries.filter(e => e.status === 'pending').map(e => e.id);
      if (pendingIds.length === 0) {
        toast.info('Aucune heure en attente');
        setValidating(null);
        return;
      }
      const response = await axios.post(`${API}/time/validate`, { entry_ids: pendingIds, action: 'validate' });
      toast.success(`${response.data.modified} heures validées`);
      await fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de la validation globale');
    } finally {
      setValidating(null);
    }
  };
  
  // Derived data
  const hasMultipleChildren = contracts.length > 1;
  
  const filteredEntries = useMemo(() => {
    if (selectedChild === 'all') return timeEntries;
    return timeEntries.filter(e => e.contract_id === selectedChild);
  }, [timeEntries, selectedChild]);
  
  const pendingEntries = filteredEntries.filter(e => e.status === 'pending');
  const validatedEntries = filteredEntries.filter(e => e.status === 'validated');
  
  // Group entries by child for summary
  const childSummaries = useMemo(() => {
    const groups = {};
    for (const entry of timeEntries) {
      const cid = entry.contract_id;
      if (!groups[cid]) {
        groups[cid] = {
          contract_id: cid,
          child_name: entry.child_name || 'Inconnu',
          total_hours: 0,
          pending: 0,
          validated: 0,
          absences: 0,
        };
      }
      const g = groups[cid];
      if (entry.is_absent) g.absences++;
      else g.total_hours += (entry.hours_worked || 0);
      if (entry.status === 'pending') g.pending++;
      if (entry.status === 'validated') g.validated++;
    }
    return Object.values(groups);
  }, [timeEntries]);
  
  // Enrich child summaries with contract data (sex, first name)
  const enrichedSummaries = useMemo(() => {
    return childSummaries.map(s => {
      const c = contracts.find(ct => ct.id === s.contract_id);
      return {
        ...s,
        child_first_name: c?.child_first_name || s.child_name,
        child_sex: c?.child_sex || 'M',
      };
    });
  }, [childSummaries, contracts]);
  
  if (loading) {
    return (
      <div className={embedded ? "space-y-6" : "container-custom py-6 space-y-6"}>
        <Skeleton className="h-10 w-48" />
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }
  
  return (
    <div className={embedded ? "space-y-6" : "container-custom py-6 space-y-6"} data-testid="time-validation-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {!embedded && (
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)' }}>
              Validation des heures
            </h1>
            <p className="text-[var(--text-secondary)] mt-1">
              Validez les heures déclarées par votre assistante maternelle
            </p>
          </div>
        )}
        {embedded && <div />}
        
        <div className="flex items-center gap-2">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[180px]" data-testid="validation-month-select">
              <Calendar className="h-4 w-4 mr-2 text-[var(--primary-400)]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {pendingEntries.length > 0 && (
            <Button
              onClick={handleValidateAll}
              disabled={validating === 'all'}
              style={{ backgroundColor: 'var(--success)' }}
              data-testid="validate-all-button"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Tout valider ({pendingEntries.length})
            </Button>
          )}
        </div>
      </div>
      
      {/* Children selector — shared component */}
      {enrichedSummaries.length > 0 && (
        <ChildFilterCards
          children={enrichedSummaries}
          selectedChild={selectedChild}
          onSelect={setSelectedChild}
          allLabel="Tous les enfants"
          allSublabel={`${timeEntries.length} entrée${timeEntries.length > 1 ? 's' : ''}`}
          renderStats={(child) => (
            <>
              <span className="text-xs font-mono font-semibold" style={{ color: genderAccent(child.child_sex) }}>
                {child.total_hours.toFixed(1)}h
              </span>
              {child.pending > 0 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)]">
                  {child.pending} en attente
                </Badge>
              )}
              {child.validated > 0 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]">
                  {child.validated} validé{child.validated > 1 ? 'es' : ''}
                </Badge>
              )}
              {child.absences > 0 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[var(--warning)]/5 text-[var(--warning)] border-[var(--warning)]/40">
                  {child.absences} abs.
                </Badge>
              )}
            </>
          )}
        />
      )}
      
      {filteredEntries.length === 0 ? (
        <Card className="border-dashed" data-testid="empty-state">
          <CardHeader>
            <CardTitle>Aucune entrée ce mois</CardTitle>
            <CardDescription>
              {selectedChild !== 'all' 
                ? 'Aucune heure enregistrée pour cet enfant ce mois-ci'
                : 'Les heures déclarées apparaîtront ici pour validation'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Pending Entries */}
          {pendingEntries.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[var(--warning)]" />
                En attente ({pendingEntries.length})
              </h2>
              {pendingEntries.map(entry => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  validating={validating}
                  onValidate={handleValidate}
                  showActions={true}
                  testIdPrefix="pending"
                  contracts={contracts}
                />
              ))}
            </div>
          )}
          
          {/* Validated Entries */}
          {validatedEntries.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-[var(--success)]" />
                Validées ({validatedEntries.length})
              </h2>
              {validatedEntries.slice(0, 10).map(entry => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  validating={validating}
                  onValidate={handleValidate}
                  showActions={false}
                  testIdPrefix="validated"
                  contracts={contracts}
                />
              ))}
              {validatedEntries.length > 10 && (
                <p className="text-sm text-[var(--text-tertiary)] text-center py-2">
                  + {validatedEntries.length - 10} autres entrées validées
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeValidation;
